import { useState } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { LogOut, Menu, X, Truck } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useLocation } from 'wouter';
import { getAccountBrand } from '@shared/branding';

interface HeaderWithLogoutProps {
  title?: string;
}

export function HeaderWithLogout({ title = "Gewgul" }: HeaderWithLogoutProps) {
  const { user, logoutMutation } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        toast({
          title: "Logged out",
          description: "You have been successfully logged out",
        });
      },
      onError: (error) => {
        toast({
          title: "Logout failed",
          description: error.message,
          variant: "destructive",
        });
      }
    });
  };

  const accountBrand = getAccountBrand(user);
  const isDealnet = accountBrand === 'dealnet';
  const isRoadMover = accountBrand === 'roadmover';
  const navigation = isRoadMover
    ? [
        { label: 'Home', href: '/app', color: '#dbe8f3' },
        { label: 'Dashboard', href: '/dashboard', color: '#dbe8f3' },
        { label: 'Dialer', href: '/dialer', color: '#dbe8f3' },
        { label: 'Search', href: '/dataaxle', color: '#dbe8f3' },
        { label: 'Opportunities', href: '/hot-leads', color: '#f2a43a', bold: true },
        { label: 'Team', href: '/dashboard', color: '#dbe8f3' },
      ]
    : [
        { label: 'Home', href: '/app', color: '#a5f3d0' },
        { label: 'Dashboard', href: '/dashboard', color: '#a5f3d0' },
        { label: '✨ Magical Search', href: '/dataaxle', color: '#93c5fd' },
        { label: '⭐ Hot Leads', href: '/hot-leads', color: '#fbbf24', bold: true },
        { label: '👥 Team', href: '/dealnet-dashboard', color: '#86efac' },
      ];

  return (
    <header
        style={isRoadMover ? {
          background: 'linear-gradient(100deg, #10243a 0%, #173451 60%, #10243a 100%)',
          borderBottom: '1px solid rgba(242, 164, 58, 0.38)',
          boxShadow: '0 2px 12px rgba(16, 36, 58, 0.18)',
        } : {
          background: 'linear-gradient(90deg, #0a1628 0%, #0f2744 40%, #0d3320 70%, #0a1628 100%)',
          borderBottom: '1px solid rgba(52, 211, 153, 0.25)',
          boxShadow: '0 2px 12px rgba(0,0,0,0.5)',
        }}
    >
      <div className="container mx-auto px-4 py-2.5 flex justify-between items-center">
        <div className="flex items-center gap-4">
          {!isDealnet && (
            isRoadMover ? (
              <button
                type="button"
                className="group flex items-center gap-2.5 rounded-md text-left transition-transform duration-200 hover:-translate-y-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#f2a43a]"
                onClick={() => setLocation('/app')}
                aria-label="RoadMover home"
              >
                <span className="grid h-9 w-9 place-items-center rounded-xl bg-[#f2a43a] text-[#10243a]">
                  <Truck size={20} strokeWidth={2.4} />
                </span>
                <span className="text-lg font-black tracking-tight text-white">
                  Road<span className="text-[#f2a43a]">Mover</span>
                </span>
              </button>
            ) : (
            <h1
              className="text-xl font-bold cursor-pointer flex items-center gap-2"
              style={{ color: '#34d399' }}
              onClick={() => setLocation('/app')}
            >
              {title}
            </h1>
            )
          )}
          <nav className="hidden md:flex items-center gap-1">
            {navigation.map(({ label, href, color, bold }) => (
              <button
                key={`${label}-${href}`}
                onClick={() => setLocation(href)}
                style={{
                  color,
                  fontWeight: bold ? 700 : 400,
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  transition: 'background 0.15s, color 0.15s, transform 0.15s',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLButtonElement).style.background = isRoadMover ? 'rgba(242,164,58,0.14)' : 'rgba(52,211,153,0.12)';
                  (e.currentTarget as HTMLButtonElement).style.color = '#ffffff';
                  (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  (e.currentTarget as HTMLButtonElement).style.color = color;
                  (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
                }}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          {isRoadMover && <button className="md:hidden p-2 rounded-md" aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'} aria-expanded={mobileOpen} onClick={() => setMobileOpen(!mobileOpen)} style={{ color: '#f5ca78' }}>{mobileOpen ? <X size={20} /> : <Menu size={20} />}</button>}
          {user && (
            <span
              className="text-sm hidden md:inline-block"
              style={{ color: isRoadMover ? 'rgba(219,232,243,0.78)' : 'rgba(134,239,172,0.7)' }}
            >
              Welcome, {user.username} ({user.role})
            </span>
          )}
          <button
            className={isRoadMover ? "hidden sm:flex" : "flex"}
            onClick={handleLogout}
            style={{
              alignItems: 'center',
              gap: '6px',
              color: isRoadMover ? '#f2a43a' : '#a5f3d0',
              background: 'transparent',
              border: isRoadMover ? '1px solid rgba(242,164,58,0.42)' : '1px solid rgba(52,211,153,0.3)',
              borderRadius: '6px',
              padding: '5px 12px',
              fontSize: '0.875rem',
              cursor: 'pointer',
              transition: 'background 0.15s, color 0.15s, border-color 0.15s, transform 0.15s',
            }}
            onMouseEnter={e => {
              const el = e.currentTarget as HTMLButtonElement;
              el.style.background = isRoadMover ? 'rgba(242,164,58,0.16)' : 'rgba(52,211,153,0.15)';
              el.style.color = '#ffffff';
              el.style.borderColor = isRoadMover ? 'rgba(242,164,58,0.75)' : 'rgba(52,211,153,0.6)';
              el.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={e => {
              const el = e.currentTarget as HTMLButtonElement;
              el.style.background = 'transparent';
              el.style.color = isRoadMover ? '#f2a43a' : '#a5f3d0';
              el.style.borderColor = isRoadMover ? 'rgba(242,164,58,0.42)' : 'rgba(52,211,153,0.3)';
              el.style.transform = 'translateY(0)';
            }}
          >
            <LogOut style={{ width: 14, height: 14 }} />
            Logout
          </button>
        </div>
      </div>
      {isRoadMover && mobileOpen && <nav className="md:hidden px-3 pb-3 grid grid-cols-2 gap-2">{navigation.map(item => <button key={`${item.label}-${item.href}`} onClick={() => { setLocation(item.href); setMobileOpen(false); }} className="text-left rounded-md px-3 py-2 text-sm" style={{ color: '#f5ca78', background: 'rgba(231,168,62,.09)' }}>{item.label}</button>)}<button onClick={handleLogout} className="text-left rounded-md px-3 py-2 text-sm sm:hidden" style={{ color: '#f5ca78', background: 'rgba(231,168,62,.09)' }}>Logout</button></nav>}
    </header>
  );
}
